import { useApolloClient } from "@apollo/client";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { UploadPhotoModal } from "../../../commons/modals/UploadPhotoModal.js";
import { Attachment } from "../../attachments/helpers.js";
import { OrganisationAvatar } from "../OrganisationAvatar.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsProfilePicturesBox_ViewerOrganisationFragment,
  useOrganisationDetailsProfilePicturesBox_ModifyOrganisationPicturesMutation,
} from "./OrganisationDetailsProfilePicturesBox.generated.js";

interface Props {
  organisation: OrganisationDetailsProfilePicturesBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}

function ProfilePicturesSection({
  organisationAvatar,
  bannerUrl,
  editable,
  avatarEditable,
  avatarLoading,
  bannerLoading,
  onAvatarUpload,
  onAvatarRemove,
  onBannerUpload,
  onBannerRemove,
}: {
  organisationAvatar: OrganisationDetailsProfilePicturesBox_ViewerOrganisationFragment;
  bannerUrl: string | null;
  editable: boolean;
  avatarEditable: boolean;
  avatarLoading: boolean;
  bannerLoading: boolean;
  onAvatarUpload: (attachment: Attachment) => Promise<void>;
  onAvatarRemove: (() => Promise<void>) | undefined;
  onBannerUpload: (attachment: Attachment) => Promise<void>;
  onBannerRemove: (() => Promise<void>) | undefined;
}) {
  const [bannerModalOpen, setBannerModalOpen] = useState(false);

  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const classesIconButtonLight = {
    backgroundColor: "common.white",
    "&:hover:not(.Mui-disabled), &.Mui-focused, &.Mui-focusVisible": {
      backgroundColor: "grey.100",
    },
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        onClick={() => {
          if (editable && !bannerLoading) setBannerModalOpen(true);
        }}
        sx={{
          position: "relative",
          borderRadius: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          height: 180,
          bgcolor: "grey.100",
          cursor: editable && !bannerLoading ? "pointer" : "default",
        }}
      >
        {bannerUrl ? (
          <Box
            component="img"
            src={bannerUrl}
            alt={t("Public profile banner", {
              ns: "OrganisationProfile",
            })}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", height: "100%", color: "text.secondary" }}
          >
            <AddAPhotoIcon fontSize="large" />
            <Typography variant="caption">
              {t("No banner uploaded", { ns: "OrganisationProfile" })}
            </Typography>
          </Stack>
        )}

        {editable && (
          <Box sx={{ position: "absolute", right: 8, bottom: 8 }}>
            <IconButton
              color="primary"
              size="large"
              disabled={bannerLoading}
              onClick={event => {
                event.stopPropagation();
                setBannerModalOpen(true);
              }}
              sx={classesIconButtonLight}
            >
              <AddAPhotoIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          marginTop: -8,
          marginLeft: 3,
          paddingBottom: 1.5,
          width: "fit-content",
          "& .MuiAvatar-root": {
            boxShadow: theme =>
              `0 0 0 4px ${theme.palette.background.paper}, 0 2px 8px rgba(0,0,0,0.15)`,
          },
        }}
      >
        <OrganisationAvatar
          organisationAvatar={organisationAvatar}
          size="xl"
          disabled={avatarLoading}
          uploadModalProps={{
            title: t("Edit public profile picture", {
              ns: "OrganisationProfile",
            }),
            initialTitle: t("Public profile picture", {
              ns: "OrganisationProfile",
            }),
          }}
          onAttachment={
            avatarEditable
              ? async attachment => {
                  if (attachment) {
                    await onAvatarUpload(attachment);
                  } else if (onAvatarRemove) {
                    await onAvatarRemove();
                  }
                }
              : undefined
          }
          cameraButtonSx={classesIconButtonLight}
        />
      </Box>

      {editable && bannerModalOpen && (
        <UploadPhotoModal
          title={t("Edit public profile banner", { ns: "OrganisationProfile" })}
          initialUrl={bannerUrl ?? undefined}
          initialTitle={t("Public profile banner", {
            ns: "OrganisationProfile",
          })}
          handleComplete={async uploaded => {
            await onBannerUpload(uploaded);
            setBannerModalOpen(false);
          }}
          handleRemove={
            onBannerRemove
              ? async () => {
                  await onBannerRemove();
                }
              : undefined
          }
          loading={bannerLoading}
          skipEdit
          handleClose={() => setBannerModalOpen(false)}
        />
      )}
    </Box>
  );
}

export function OrganisationDetailsProfilePicturesBox({
  organisation,
  isViewerOrganisation,
}: Props) {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const [profileImage, setProfileImage] = useState<Attachment | null>(
    organisation.logo ?? null
  );
  const [profileBanner, setProfileBanner] = useState<Attachment | null>(
    organisation.bannerImage ?? null
  );

  useEffect(() => {
    setProfileImage(organisation.logo ?? null);
    setProfileBanner(organisation.bannerImage ?? null);
  }, [organisation.logo, organisation.bannerImage]);

  const organisationAvatar = useMemo(() => organisation, [organisation]);

  const [modifyPictures, { loading: modifyPicturesLoading }] =
    useOrganisationDetailsProfilePicturesBox_ModifyOrganisationPicturesMutation(
      {
        client,
        refetchQueries: [namedOperations.Query.OrganisationDetails],
      }
    );

  async function onLogoAttachment(attachment: Attachment | null) {
    try {
      await modifyPictures({
        variables: {
          input: {
            organisationId: organisation.id,
            logo: attachment
              ? {
                  url: attachment.url,
                  title: attachment.title,
                  mimeType: attachment.mimeType,
                }
              : null,
          },
        },
      });

      setProfileImage(attachment);
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  }

  async function onBannerAttachment(attachment: Attachment | null) {
    try {
      await modifyPictures({
        variables: {
          input: {
            organisationId: organisation.id,
            bannerImage: attachment
              ? {
                  url: attachment.url,
                  title: attachment.title,
                  mimeType: attachment.mimeType,
                }
              : null,
          },
        },
      });
      setProfileBanner(attachment);
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  }

  return (
    <SectionCard title={t("Profile pictures", { ns: "OrganisationProfile" })}>
      <Box sx={{ pb: 1, position: "relative" }}>
        <RestrictedByOrganisationPermissionWithDebug
          permission="MANAGE_ORG"
          otherwise={
            <ProfilePicturesSection
              organisationAvatar={organisationAvatar}
              bannerUrl={profileBanner?.url ?? null}
              editable={false}
              avatarEditable={false}
              avatarLoading={false}
              bannerLoading={false}
              onAvatarUpload={async () => undefined}
              onAvatarRemove={undefined}
              onBannerUpload={async () => undefined}
              onBannerRemove={undefined}
            />
          }
        >
          <ProfilePicturesSection
            organisationAvatar={organisationAvatar}
            bannerUrl={profileBanner?.url ?? null}
            editable
            avatarEditable={isViewerOrganisation}
            avatarLoading={modifyPicturesLoading}
            bannerLoading={modifyPicturesLoading}
            onAvatarUpload={async attachment => {
              await onLogoAttachment(attachment);
            }}
            onAvatarRemove={
              profileImage
                ? async () => {
                    await onLogoAttachment(null);
                  }
                : undefined
            }
            onBannerUpload={async attachment => {
              await onBannerAttachment(attachment);
            }}
            onBannerRemove={
              profileBanner
                ? async () => {
                    await onBannerAttachment(null);
                  }
                : undefined
            }
          />
        </RestrictedByOrganisationPermissionWithDebug>
      </Box>
    </SectionCard>
  );
}
